/**
 * useIdempotencyKey — the single idempotency-key lifecycle for member writes.
 *
 * The back office dedupes a write by its `idempotency-key`, so the key IS the
 * definition of "the same save". That makes the lifecycle a correctness rule, not
 * a detail (035 FR-006):
 *
 *   - **mint** one key per save-intent;
 *   - **freeze** it across retries of an UNCHANGED payload, so a double-tap or a
 *     retry-after-failure applies exactly once;
 *   - **rebind** to a fresh key when the payload changes, so a genuinely different
 *     resubmit is honoured rather than silently swallowed as a duplicate;
 *   - **clear** on success, so the next Save is a new intent (reusing the key there
 *     would make the back office dedupe a legitimate re-save of the same values).
 *
 * It lives in one place deliberately. Contact and consent both need the rule, and a
 * per-surface copy is how it rots — one surface freezes, the other regenerates, and
 * a double-tap double-applies on exactly one of them, which no per-surface test
 * would catch. The caller supplies the payload comparison because only the caller
 * knows what "the same save" means for its shape (the five contact fields; the
 * consent boolean). No default comparator is offered on purpose: a structural
 * default would be wrong for objects in the dangerous direction — it would mint a
 * new key on every attempt and quietly defeat deduplication.
 *
 * The key is held in a ref, not state: nothing renders it, so recording it must not
 * trigger a re-render (and must be visible to the very next call within the same
 * event handler).
 *
 * Owned by feature 035-member-profile-edit.
 * Tasks: T011 (contact), T029 (consent).
 */

import { useRef } from 'react';

/** Handle over one save-intent's key. */
export interface IdempotencyKeyHandle<TPayload> {
  /**
   * The key to send for `payload`: the held key when `payload` is unchanged since
   * the last call, otherwise a freshly minted one (which then becomes the held key).
   */
  readonly keyFor: (payload: TPayload) => string;
  /** End the current save-intent. Call after a write succeeds. */
  readonly clear: () => void;
}

/**
 * @param isUnchanged Returns true when two payloads represent the SAME save. Called
 * with (heldPayload, nextPayload). Getting this wrong toward "always changed" breaks
 * dedup (double-submit applies twice); toward "always unchanged" swallows real edits.
 */
export function useIdempotencyKey<TPayload>(
  isUnchanged: (previous: TPayload, next: TPayload) => boolean,
): IdempotencyKeyHandle<TPayload> {
  const held = useRef<{ key: string; payload: TPayload } | null>(null);

  // Plain handlers — the React Compiler memoizes them, and neither is used as an
  // effect dependency, so a manual useCallback would be the anti-pattern the
  // react-compiler-uniformity check forbids.
  const keyFor = (payload: TPayload): string => {
    const current = held.current;
    if (current !== null && isUnchanged(current.payload, payload)) {
      return current.key;
    }
    // `crypto.randomUUID()` is 36 chars, comfortably over the router's `min(8)`
    // validation, and needs no server round trip to mint.
    const key = crypto.randomUUID();
    held.current = { key, payload };
    return key;
  };

  const clear = (): void => {
    held.current = null;
  };

  return { keyFor, clear };
}
